<template>
  <a
      v-if="state === 'booked'"
      :href="bookedHref"
      target="_blank"
      rel="noopener"
      class="action-chip chip-booked"
      :title="tooltip"
      @click.stop
  >
    <svg class="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none">
      <rect x="2.5" y="5.5" width="7" height="5" rx="1" stroke="currentColor" stroke-width="1.3" fill="none" />
      <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" stroke="currentColor" stroke-width="1.3" fill="none" stroke-linecap="round" />
    </svg>
    <span class="whitespace-nowrap">{{ label }}</span>
    <svg class="h-2.5 w-2.5 opacity-70" viewBox="0 0 10 10" fill="none">
      <path d="M4 1h5v5M9 1 4 6M2 3v5h5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  </a>

  <span
      v-else-if="state === 'locked'"
      class="action-chip chip-locked"
      :title="tooltip"
  >
    <svg class="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none">
      <rect x="2.5" y="5.5" width="7" height="5" rx="1" stroke="currentColor" stroke-width="1.3" fill="none" />
      <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" stroke="currentColor" stroke-width="1.3" fill="none" stroke-linecap="round" />
    </svg>
    <span class="whitespace-nowrap">{{ label }}</span>
  </span>

  <button
      v-else
      type="button"
      class="action-chip"
      :class="chipClass"
      :title="tooltip"
      @click="onClick"
  >
    <svg v-if="state === 'selected'" class="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none">
      <path d="M2 6.5 4.8 9.2 10 3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    <span v-else class="cell-plus">+</span>
    <span class="whitespace-nowrap">{{ label }}</span>
  </button>
</template>

<script>
import { categoryPillState, lockedScheduleJobId } from "@/utils/equipmentLocks";

const TITLE = {
  service: 'Service',
  calibration: 'Calibration',
  repair: 'Repair',
  warranty: 'Warranty',
  install: 'Install',
};

export default {
  name: "ActionCell",
  props: {
    category: { type: String, required: true },
    equipment: { type: Object, default: null },
    scheduleDetail: { type: Object, default: null },
    currentScheduleId: { type: String, default: null },
    readonly: { type: Boolean, default: false },
  },
  computed: {
    state() {
      const state = categoryPillState(
        this.equipment,
        this.category,
        this.currentScheduleId,
        this.scheduleDetail,
      );
      // A schedule that has moved past NotStarted cannot be changed, so
      // anything on it shows as locked rather than removable.
      if (this.readonly && state === 'selected') return 'locked';
      return state;
    },
    chipClass() {
      switch (this.state) {
        case 'selected': return 'chip-selected';
        case 'due':      return 'chip-due';
        default:         return 'chip-free';
      }
    },
    bookedScheduleId() {
      return this.equipment && this.equipment[`active_${this.category}_schedule_id`] || null;
    },
    bookedJobId() {
      return this.equipment && this.equipment[`active_${this.category}_schedule_job_id`] || null;
    },
    lockedJobId() {
      return lockedScheduleJobId(this.equipment, this.category, this.scheduleDetail);
    },
    label() {
      switch (this.state) {
        case 'selected': return 'Added';
        case 'locked':   return this.lockedJobId || 'Locked';
        case 'booked':   return this.bookedJobId || 'Booked';
        default:         return 'Add';
      }
    },
    bookedHref() {
      if (!this.bookedScheduleId) return '#';
      const resolved = this.$router.resolve({
        name: 'schedule-edit',
        params: { schedule_id: this.bookedScheduleId },
      });
      return resolved && resolved.href ? resolved.href : '#';
    },
    tooltip() {
      const name = TITLE[this.category] || this.category;
      switch (this.state) {
        case 'selected': return `${name} added to this schedule — click to remove.`;
        case 'locked':
          if (this.readonly) return 'This schedule is no longer editable.';
          return this.lockedJobId ? `Locked on this schedule · Job ${this.lockedJobId}` : `Locked on this schedule.`;
        case 'booked':   return this.bookedJobId
            ? `Booked on Job ${this.bookedJobId} — open in new tab`
            : 'Booked on another schedule — open in new tab';
        case 'due':      return `${name} is due — click to add.`;
        default:         return `Click to add ${name.toLowerCase()} to this schedule.`;
      }
    },
  },
  methods: {
    onClick() {
      if (this.readonly) return;
      if (this.state === 'locked' || this.state === 'booked') return;
      const nextValue = !(this.state === 'selected');
      this.$emit('toggle', { category: this.category, value: nextValue });
    },
  },
};
</script>

<style scoped>
.action-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;
  font-weight: 600;
  white-space: nowrap;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  transition: background-color 120ms, border-color 120ms, color 120ms, box-shadow 120ms;
  text-decoration: none;
}
.cell-plus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #9CA3AF;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  transition: background-color 120ms;
}

.chip-free {
  border: 1px dashed #D1D5DB;
  color: #6B7280;
  background: #ffffff;
}
.chip-free:hover {
  border-color: #FF9B00;
  color: #AE5F00;
  background: #FFFAF3;
}
.chip-free:hover .cell-plus { background: #FF9B00; }

.chip-due {
  border: 1px solid #F2C664;
  background: #FFF7E1;
  color: #8A5A00;
}
.chip-due .cell-plus { background: #FF9B00; }
.chip-due:hover {
  background: #FFECBF;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.chip-selected {
  background: #FF9B00;
  border: 1px solid #FF9B00;
  color: #ffffff;
}
.chip-selected:hover {
  background: #E88A1A;
  border-color: #E88A1A;
}

.chip-booked {
  background: #F4F6F8;
  border: 1px dashed #D6DAE0;
  color: #374151;
  font-weight: 500;
}
.chip-booked:hover {
  background: #EEF2F7;
  color: #1E40AF;
  border-color: #BFDBFE;
}

.chip-locked {
  background: #FDE9D1;
  border: 1px solid #F0B363;
  color: #92400E;
  cursor: not-allowed;
}
</style>
